import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import withComponentAuth from '../../hoc/withComponentAuth';
import { SmartFilter } from '../../components/common/SmartFilter';
import { CreateTableHeader } from '../../components/common/TableHeader';
import { headerLeavesTable } from '../../utils/constants';
import Pagination from '../../components/atoms/Pagination';
import { useNavigate } from 'react-router-dom';
import { GETLeaves, ILeave, PATCHLeaveStatus } from '../../services/leave';
import { debounce } from '../../utils/helper';
import DateFilterComp, {
  DateFilterData,
} from '../../components/ui/filters/enquiry';
import useSnackbar from '../../hooks/useSnackbar';
import AddLeaveRequestForm from '../../components/ui/add/leave';
import { LeaveList } from '../../components/ui/list/Leave';

// Groups leaves (as returned, already in the page's current sort order) by the
// calendar month of `startDate` — preserves the existing relative ordering, it
// just inserts a month header wherever the month changes.
interface LeaveMonthGroup {
  key: string;
  label: string;
  items: ILeave[];
  statusCounts: Record<'PENDING' | 'APPROVED' | 'REJECTED', number>;
}

const groupLeavesByMonth = (leaves: ILeave[]): LeaveMonthGroup[] => {
  const groups: LeaveMonthGroup[] = [];
  const indexByKey = new Map<string, number>();

  leaves.forEach((leave) => {
    const parsed = dayjs(leave.startDate);
    const key = parsed.isValid() ? parsed.format('YYYY-MM') : 'unknown';
    const label = parsed.isValid() ? parsed.format('MMMM YYYY') : 'Unknown date';

    let idx = indexByKey.get(key);
    if (idx === undefined) {
      idx = groups.length;
      indexByKey.set(key, idx);
      groups.push({ key, label, items: [], statusCounts: { PENDING: 0, APPROVED: 0, REJECTED: 0 } });
    }
    groups[idx].items.push(leave);
    const status = leave.status;
    if (status === 'PENDING' || status === 'APPROVED' || status === 'REJECTED') {
      groups[idx].statusCounts[status] += 1;
    }
  });

  return groups;
};

const STATUS_META: Record<'PENDING' | 'APPROVED' | 'REJECTED', { label: string; color: string }> = {
  APPROVED: { label: 'approved', color: '#10b981' },
  PENDING: { label: 'pending', color: '#f59e0b' },
  REJECTED: { label: 'rejected', color: '#ef4444' },
};

const monthAccordionSx = {
  background: '#171717',
  color: '#fff',
  borderRadius: '10px !important',
  mb: 2,
  border: '1px solid #242424',
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: '0 0 16px 0' },
};

const monthAccordionSummarySx = {
  minHeight: 56,
  px: 2,
  position: 'sticky' as const,
  top: 0,
  zIndex: 2,
  backgroundColor: '#171717',
  borderRadius: '10px',
  '& .MuiAccordionSummary-content': {
    my: 1,
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap' as const,
  },
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
  },
};

const LeavesandHolidaysComp = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [leaves, setLeaves] = useState<ILeave[]>([]);
  const [totalLeaves, setTotalLeaves] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sort, setSort] = useState<'ASC' | 'DESC'>('DESC');
  const [tabValue, setTabValue] = useState<string>('ALL');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterFlag, setFilterFlag] = useState<boolean>(false);
  const { showSnackbar, SnackbarComponent, resetSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const fetchLeaves = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
    leaveType?: string,
    startDate?: Date | null,
    endDate?: Date | null,
  ) => {
    try {
      setIsLoading(true);
      const response = await GETLeaves({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
        leaveType,
        startDate,
        endDate,
      });

      // Ensure data is an array
      setLeaves(Array.isArray(response?.data) ? response.data : []);
      setTotalLeaves(response?.count || 0);
    } catch (error) {
      console.error('Failed to fetch leaves:', error);
      setLeaves([]);
      setTotalLeaves(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const { startDate, endDate } = getDateRange(tabValue);
    if (!filterFlag) {
      fetchLeaves(
        page,
        rowsPerPage,
        searchQuery,
        sort,
        GetTabFilter(tabValue),
        startDate,
        endDate,
      );
    }
  }, [page, rowsPerPage, searchQuery, sort, tabValue]);

  useEffect(() => {
    if (filterFlag) {
      fetchLeaves(
        page,
        rowsPerPage,
        searchQuery,
        sort,
        GetTabFilter(tabValue),
        startDate,
        endDate,
      );
    }
  }, [startDate, endDate, page, rowsPerPage, searchQuery, sort, tabValue]);

  const reloadDefaultList = () => {
    const { startDate, endDate } = getDateRange(tabValue);
    setStartDate(null);
    setEndDate(null);
    setFilterFlag(false);
    fetchLeaves(
      page,
      rowsPerPage,
      searchQuery,
      sort,
      GetTabFilter(tabValue),
      startDate,
      endDate,
    );
  };

  const handleSaveRefresh = () => {
    reloadDefaultList();
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1); // API expects 1-based page index
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleOpenManageDrawer = async (id: string, action: string) => {
    if (!id) {
      showSnackbar('Invalid leave ID', 'error');
      return;
    }

    if (action === 'VIEW') {
      navigate(`/admin/technician/${id}/leave-details`);
    } else if (action === 'APPROVED' || action === 'REJECTED') {
      try {
        await PATCHLeaveStatus(id, action);
        reloadDefaultList();
        showSnackbar(`Leave ${action.toLowerCase()} successfully`, 'success');
      } catch (err: any) {
        showSnackbar(err?.message || 'Action failed', 'error');
      }
    }
  };

  const handleTabChange = (event: any, newValue: string) => {
    setFilterFlag(false);
    setStartDate(null);
    setEndDate(null);
    setTabValue(newValue);
    setPage(1);
  };

  const leaveGroups = useMemo(() => groupLeavesByMonth(leaves), [leaves]);

  // Current month starts expanded; older months start collapsed. Manual
  // toggles are tracked as overrides so they survive a background refetch
  // (e.g. after approving/rejecting a request) instead of resetting.
  const currentMonthKey = useMemo(() => dayjs().format('YYYY-MM'), []);
  const [expandOverrides, setExpandOverrides] = useState<Record<string, boolean>>({});
  const isMonthExpanded = (key: string) =>
    expandOverrides[key] !== undefined ? expandOverrides[key] : key === currentMonthKey;
  const toggleMonth = (key: string) =>
    setExpandOverrides((prev) => ({ ...prev, [key]: !isMonthExpanded(key) }));

  const handleFilterRefresh = (Filter: DateFilterData) => {
    if (Filter.startDate && Filter.endDate) {
      setStartDate(new Date(Filter.startDate));
      setEndDate(new Date(Filter.endDate));
      setFilterFlag(true);
    } else if (Filter.startDate) {
      setStartDate(new Date(Filter.startDate));
      setFilterFlag(true);
    } else if (Filter.endDate) {
      setEndDate(new Date(Filter.endDate));
      setFilterFlag(true);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}

      <Box marginBottom={'10px'}>
        <SmartFilter
          title="Leaves"
          createFilter={['SEARCH', 'FILTER', 'ADD']}
          filterHeader={'Filter Services'}
          addbuttontitle="Request Leave"
          addHeader={'Request Leave'}
          addElement={<AddLeaveRequestForm />}
          filterElement={
            <DateFilterComp
              tabValue={tabValue}
              onResetFilter={reloadDefaultList}
              startDate={
                startDate
                  ? new Date(startDate).toISOString().split('T')[0]
                  : null
              }
              endDate={
                endDate ? new Date(endDate).toISOString().split('T')[0] : null
              }
            />
          }
          onSaved={handleSaveRefresh}
          onSearch={handleSearchRefresh}
          onFiltered={handleFilterRefresh}
          addDrawerSize="md"
        />
      </Box>
      <Box display="flex" justifyContent="center" width="100%" my={1}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={tabStyles}>
          <Tab value="ALL" label="All Requests" />
          <Tab value="SICK_LEAVE" label="Sick Leave" />
          <Tab value="CASUAL_LEAVE" label="Casual Leave" />
          <Tab value="PAID_LEAVE" label="Paid Leave" />
        </Tabs>
      </Box>

      <Box
        sx={{
          height: '100%',
          maxHeight: '100%',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
          p:1
        }}
      >
        {!isLoading && totalLeaves === 0 ? (
          <Typography
            textAlign={'center'}
            marginTop={'20%'}
            color="textSecondary"
          >
            No Leave Request Found
          </Typography>
        ) : isLoading ? (
          <LeaveList
            Leaves={[]}
            headerCells={headerLeavesTable || []}
            isLoading
            onManageClick={handleOpenManageDrawer}
          />
        ) : (
          <Box>
            {leaveGroups.map((group) => (
              <Accordion
                key={group.key}
                expanded={isMonthExpanded(group.key)}
                onChange={() => toggleMonth(group.key)}
                sx={monthAccordionSx}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#8A8D91' }} />}
                  sx={monthAccordionSummarySx}
                >
                  <Typography color="#fff" fontWeight={700} fontSize={15}>
                    {group.label}
                  </Typography>
                  <Chip
                    label={`${group.items.length} request${group.items.length === 1 ? '' : 's'}`}
                    size="small"
                    sx={{ bgcolor: '#7B1FA222', color: '#7B1FA2', fontWeight: 600 }}
                  />
                  {(['APPROVED', 'PENDING', 'REJECTED'] as const).map((status) =>
                    group.statusCounts[status] > 0 ? (
                      <Typography key={status} fontSize={12.5} color="#8A8D91">
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: STATUS_META[status].color,
                            mr: 0.75,
                          }}
                        />
                        {group.statusCounts[status]} {STATUS_META[status].label}
                      </Typography>
                    ) : null,
                  )}
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <LeaveList
                    Leaves={group.items}
                    headerCells={headerLeavesTable || []}
                    isLoading={false}
                    onManageClick={handleOpenManageDrawer}
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Box>
      <Box display={'block'} bottom={0} right={0} mt={2}>
        <Pagination
          count={totalLeaves}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

export default withComponentAuth(LeavesandHolidaysComp);

export const tabStyles = {
  backgroundColor: '#1B1B1B',
  borderRadius: '8px',
  padding: '6px',
  display: 'inline-flex',
  '& .MuiTabs-flexContainer': {
    justifyContent: 'center',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    color: '#fff',
    textTransform: 'none',
    fontSize: '14px',
    padding: '10px 16px',
    minWidth: 'unset',
  },
  '& .Mui-selected': {
    color: '#fff !important',
    borderRadius: '8px',
    backgroundColor: '#7B1FA2',
  },
};

const getDateRange = (tabValue: string) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  switch (tabValue) {
    case 'TODAY':
      return {
        startDate: today,
        endDate: today,
      };
    case 'OPEN_CALLS':
      return {
        startDate: null,
        endDate: yesterday,
      };
    default:
      return { startDate: null, endDate: null };
  }
};

const GetTabFilter = (tabValue: string) => {
  switch (tabValue) {
    case 'ALL':
      return 'SICK_LEAVE,CASUAL_LEAVE,PAID_LEAVE';
    case 'SICK_LEAVE':
      return 'SICK_LEAVE';
    case 'CASUAL_LEAVE':
      return 'CASUAL_LEAVE';
    case 'PAID_LEAVE':
      return 'PAID_LEAVE';
    default:
      return '';
  }
};
